import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RoleBadge } from '@/auth/RoleBadge';
import { StatusBadge } from './StatusBadge';
import type { AccountViewResponseDTO } from '../types';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/**
 * Read-only admin summary. Mutating actions (Edit / Enable / Disable / Delete) were removed —
 * admin lifecycle is owned by Microsoft Entra (TM-3368).
 */
export function AdminSummaryCard({ admin }: { admin: AccountViewResponseDTO }) {
  const roles = admin.roleResponseDTOS ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{admin.displayName || admin.email || 'Admin account'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Email">{admin.email || '—'}</Field>
          <Field label="Status">
            <StatusBadge status={admin.accountStatus} />
          </Field>
          <Field label="Roles">
            {roles.length === 0 ? (
              <span className="text-muted-foreground">—</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {roles.map((r) => (
                  <RoleBadge key={r.id ?? r.code ?? r.name} role={r.code ?? r.name ?? ''} />
                ))}
              </div>
            )}
          </Field>
          {/* `createdAt`/`updatedAt` dropped — not in real DTO. Phone number is the closest field. */}
          <Field label="Phone">{admin.phoneNumber || '—'}</Field>
        </div>
      </CardContent>
    </Card>
  );
}
